/**
 * Meeting Rooms Management Tab
 * Handles meeting room inventory, bookings, and availability
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { useCart } from '@/contexts/CartContext';
import { 
  Users, 
  DollarSign, 
  Clock, 
  Calendar,
  Plus, 
  Edit, 
  Trash2, 
  Projector, 
  Wifi, 
  Coffee,
  CheckCircle,
  AlertCircle,
  Building,
  MapPin,
  Phone,
  Mail,
  Loader2,
  ShoppingCart
} from 'lucide-react';
import { BACKEND_URL } from '@/lib/backendUrl';


export function MeetingRoomsTab({ hotelId, hotelName, currentUser }) {
  const [loading, setLoading] = useState(true);
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [activeView, setActiveView] = useState('rooms'); // rooms, bookings, analytics
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showAddRoomDialog, setShowAddRoomDialog] = useState(false);
  const [showAddBookingDialog, setShowAddBookingDialog] = useState(false);

  useEffect(() => {
    loadData();
  }, [hotelId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [roomsRes, bookingsRes, analyticsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/meeting-rooms/rooms/${hotelId}`),
        fetch(`${BACKEND_URL}/api/meeting-rooms/bookings/${hotelId}`),
        fetch(`${BACKEND_URL}/api/meeting-rooms/analytics/${hotelId}`)
      ]);

      const roomsData = await roomsRes.json();
      const bookingsData = await bookingsRes.json();
      const analyticsData = await analyticsRes.json();

      if (roomsData.success) setRooms(roomsData.rooms || []);
      if (bookingsData.success) setBookings(bookingsData.bookings || []);
      if (analyticsData.success) setAnalytics(analyticsData.analytics || null);
    } catch (error) {
      console.error('Failed to load meeting rooms data:', error);
      toast.error('Failed to load meeting rooms');
    } finally {
      setLoading(false);
    }
  };

  const getRoomTypeColor = (type) => {
    const colors = {
      'boardroom': 'bg-blue-100 text-blue-800 border-blue-300',
      'conference': 'bg-purple-100 text-purple-800 border-purple-300',
      'banquet': 'bg-pink-100 text-pink-800 border-pink-300',
      'small': 'bg-green-100 text-green-800 border-green-300',
      'breakout': 'bg-yellow-100 text-yellow-800 border-yellow-300'
    };
    return colors[type] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getBookingStatusColor = (status) => {
    const colors = {
      'confirmed': 'bg-blue-100 text-blue-800',
      'checked_in': 'bg-green-100 text-green-800',
      'completed': 'bg-gray-100 text-gray-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold heading-font">Meeting Rooms</h2>
            <p className="text-sm text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="meeting-rooms-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold heading-font">Meeting Rooms</h2>
          <p className="text-sm text-muted-foreground">
            Manage meeting spaces and bookings
          </p>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={() => setShowAddRoomDialog(true)}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            data-testid="add-room-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Room
          </Button>
          <Button 
            onClick={() => setShowAddBookingDialog(true)}
            variant="outline"
            data-testid="add-booking-button"
          >
            <Calendar className="w-4 h-4 mr-2" />
            New Booking
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Rooms</p>
                <p className="text-3xl font-bold heading-font">{rooms.length}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Building className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Today's Bookings</p>
                <p className="text-3xl font-bold heading-font">
                  {bookings.filter(b => b.booking_date === new Date().toISOString().split('T')[0] && b.status !== 'cancelled').length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Capacity</p>
                <p className="text-3xl font-bold heading-font">
                  {rooms.reduce((sum, room) => sum + room.capacity, 0)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Bookings</p>
                <p className="text-3xl font-bold heading-font">
                  {bookings.filter(b => b.status === 'confirmed' || b.status === 'checked_in').length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeView} onValueChange={setActiveView}>
        <TabsList>
          <TabsTrigger value="rooms" data-testid="rooms-view-tab">
            <Building className="w-4 h-4 mr-2" />
            Rooms ({rooms.length})
          </TabsTrigger>
          <TabsTrigger value="bookings" data-testid="bookings-view-tab">
            <Calendar className="w-4 h-4 mr-2" />
            Bookings ({bookings.filter(b => b.status !== 'cancelled').length})
          </TabsTrigger>
          <TabsTrigger value="analytics" data-testid="analytics-view-tab">
            <DollarSign className="w-4 h-4 mr-2" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Rooms View */}
        <TabsContent value="rooms">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rooms.map((room) => (
              <Card key={room.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{room.name}</CardTitle>
                      <Badge className={`mt-2 ${getRoomTypeColor(room.room_type)}`}>
                        {room.room_type}
                      </Badge>
                    </div>
                    <Badge variant={room.status === 'active' ? 'default' : 'secondary'}>
                      {room.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Users className="w-4 h-4 mr-2" />
                      Capacity: <span className="font-semibold ml-1">{room.capacity} people</span>
                    </div>
                    <div className="flex items-center text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4 mr-2" />
                      Floor: <span className="font-semibold ml-1">{room.floor}</span>
                    </div>
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Building className="w-4 h-4 mr-2" />
                      Size: <span className="font-semibold ml-1">{room.size_sqft} sq ft</span>
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <p className="text-sm font-semibold mb-2">Pricing:</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Hourly:</span>
                        <span className="font-semibold mono">${room.pricing.hourly_rate}/hr</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Half Day:</span>
                        <span className="font-semibold mono">${room.pricing.half_day_rate}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Full Day:</span>
                        <span className="font-semibold mono">${room.pricing.full_day_rate}</span>
                      </div>
                    </div>
                  </div>

                  {room.amenities && room.amenities.length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <p className="text-sm font-semibold mb-2">Amenities:</p>
                        <div className="flex flex-wrap gap-2">
                          {room.amenities.slice(0, 4).map((amenity, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {amenity.name}
                            </Badge>
                          ))}
                          {room.amenities.length > 4 && (
                            <Badge variant="outline" className="text-xs">
                              +{room.amenities.length - 4} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => {
                        setSelectedRoom(room);
                        setShowAddBookingDialog(true);
                      }}
                    >
                      <Calendar className="w-4 h-4 mr-1" />
                      Book
                    </Button>
                    <Button variant="outline" size="sm">
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {rooms.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <Building className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Meeting Rooms Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Start by adding your first meeting room
                </p>
                <Button onClick={() => setShowAddRoomDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Meeting Room
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Bookings View */}
        <TabsContent value="bookings">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-4 text-sm font-semibold">Guest</th>
                      <th className="text-left p-4 text-sm font-semibold">Room</th>
                      <th className="text-left p-4 text-sm font-semibold">Date & Time</th>
                      <th className="text-left p-4 text-sm font-semibold">Event Type</th>
                      <th className="text-left p-4 text-sm font-semibold">Attendees</th>
                      <th className="text-left p-4 text-sm font-semibold">Price</th>
                      <th className="text-left p-4 text-sm font-semibold">Status</th>
                      <th className="text-left p-4 text-sm font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.filter(b => b.status !== 'cancelled').map((booking) => (
                      <tr key={booking.id} className="border-t hover:bg-muted/30 transition-colors">
                        <td className="p-4">
                          <div>
                            <p className="font-semibold">{booking.guest_name}</p>
                            {booking.company_name && (
                              <p className="text-sm text-muted-foreground">{booking.company_name}</p>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <p className="font-semibold">{booking.room_name}</p>
                        </td>
                        <td className="p-4">
                          <div>
                            <p className="font-semibold">{booking.booking_date}</p>
                            <p className="text-sm text-muted-foreground">
                              {booking.start_time} - {booking.end_time}
                            </p>
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge variant="outline">{booking.event_type}</Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center">
                            <Users className="w-4 h-4 mr-1 text-muted-foreground" />
                            {booking.attendees}
                          </div>
                        </td>
                        <td className="p-4">
                          <p className="font-semibold mono">${booking.total_price}</p>
                          <p className="text-xs text-muted-foreground">{booking.booking_type}</p>
                        </td>
                        <td className="p-4">
                          <Badge className={getBookingStatusColor(booking.status)}>
                            {booking.status}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm">
                              View
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {bookings.filter(b => b.status !== 'cancelled').length === 0 && (
                <div className="py-12 text-center">
                  <Calendar className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Bookings Yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Start by creating your first meeting room booking
                  </p>
                  <Button onClick={() => setShowAddBookingDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Booking
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics View */}
        <TabsContent value="analytics">
          {analytics ? (
            <div className="space-y-6">
              {/* Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <DollarSign className="w-8 h-8 mx-auto text-green-600 mb-2" />
                      <p className="text-sm text-muted-foreground">Total Revenue</p>
                      <p className="text-2xl font-bold heading-font mono">${analytics.overview.total_revenue}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <Calendar className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                      <p className="text-sm text-muted-foreground">Total Bookings</p>
                      <p className="text-2xl font-bold heading-font">{analytics.overview.total_bookings}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <Building className="w-8 h-8 mx-auto text-purple-600 mb-2" />
                      <p className="text-sm text-muted-foreground">Active Rooms</p>
                      <p className="text-2xl font-bold heading-font">{analytics.overview.total_rooms}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <Clock className="w-8 h-8 mx-auto text-orange-600 mb-2" />
                      <p className="text-sm text-muted-foreground">Utilization Rate</p>
                      <p className="text-2xl font-bold heading-font">{analytics.overview.utilization_rate}%</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <DollarSign className="w-8 h-8 mx-auto text-pink-600 mb-2" />
                      <p className="text-sm text-muted-foreground">Avg Booking</p>
                      <p className="text-2xl font-bold heading-font mono">${analytics.overview.avg_booking_value}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Revenue by Room */}
              <Card>
                <CardHeader>
                  <CardTitle>Revenue by Room</CardTitle>
                  <CardDescription>Performance comparison across meeting spaces</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(analytics.revenue_by_room).map(([roomName, revenue]) => {
                      const bookingsCount = analytics.bookings_by_room[roomName] || 0;
                      const maxRevenue = Math.max(...Object.values(analytics.revenue_by_room));
                      const percentage = maxRevenue > 0 ? (revenue / maxRevenue) * 100 : 0;
                      
                      return (
                        <div key={roomName} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="font-semibold">{roomName}</span>
                            <span className="text-muted-foreground">
                              ${revenue.toFixed(2)} ({bookingsCount} bookings)
                            </span>
                          </div>
                          <div className="h-3 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-green-500 to-emerald-600 transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Booking Types & Event Types */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Booking Types</CardTitle>
                    <CardDescription>Distribution by duration</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(analytics.booking_types).map(([type, count]) => {
                        const total = Object.values(analytics.booking_types).reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? (count / total) * 100 : 0;
                        const icon = type === 'hourly' ? '⏰' : type === 'half_day' ? '🕐' : '📅';
                        
                        return (
                          <div key={type} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl">{icon}</span>
                              <span className="font-semibold capitalize">{type.replace('_', ' ')}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-muted-foreground">{count} bookings</span>
                              <Badge variant="outline">{percentage.toFixed(0)}%</Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Event Types</CardTitle>
                    <CardDescription>Popular meeting purposes</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(analytics.event_types).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([type, count]) => {
                        const total = Object.values(analytics.event_types).reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? (count / total) * 100 : 0;
                        
                        return (
                          <div key={type} className="flex items-center justify-between">
                            <span className="font-semibold capitalize">{type.replace('_', ' ')}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-muted-foreground">{count} bookings</span>
                              <Badge variant="outline">{percentage.toFixed(0)}%</Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Peak Hours */}
              <Card>
                <CardHeader>
                  <CardTitle>Peak Booking Hours</CardTitle>
                  <CardDescription>Most popular start times</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
                    {Array.from({ length: 24 }, (_, i) => {
                      const count = analytics.hour_distribution[i] || 0;
                      const maxCount = Math.max(...Object.values(analytics.hour_distribution));
                      const intensity = maxCount > 0 ? (count / maxCount) : 0;
                      
                      return (
                        <div key={i} className="text-center">
                          <div 
                            className="h-20 rounded-lg border-2 border-primary/20 flex items-end justify-center transition-all hover:border-primary"
                            style={{ 
                              background: `linear-gradient(to top, hsl(var(--primary)) ${intensity * 100}%, transparent ${intensity * 100}%)`,
                              opacity: count > 0 ? 1 : 0.3
                            }}
                          >
                            {count > 0 && (
                              <span className="text-xs font-bold text-primary-foreground mb-1">{count}</span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{i}:00</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Top Performer */}
              <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">🏆 Top Performing Room</p>
                      <p className="text-3xl font-bold heading-font">{analytics.top_room.name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground mb-1">Revenue Generated</p>
                      <p className="text-3xl font-bold heading-font mono text-green-600">${analytics.top_room.revenue}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">Loading analytics...</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Room Dialog */}
      <AddRoomDialog 
        open={showAddRoomDialog}
        onClose={() => setShowAddRoomDialog(false)}
        hotelId={hotelId}
        onSuccess={loadData}
      />

      {/* Add Booking Dialog */}
      <AddBookingDialog 
        open={showAddBookingDialog}
        onClose={() => {
          setShowAddBookingDialog(false);
          setSelectedRoom(null);
        }}
        hotelId={hotelId}
        hotelName={hotelName}
        rooms={rooms}
        selectedRoom={selectedRoom}
        currentUser={currentUser}
        onSuccess={loadData}
      />
    </div>
  );
}

// Add Room Dialog Component
function AddRoomDialog({ open, onClose, hotelId, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    room_type: 'boardroom',
    capacity: 10,
    description: '',
    floor: 'Ground',
    size_sqft: 200,
    hourly_rate: 50,
    half_day_rate: 180,
    full_day_rate: 300,
    setup_fee: 25,
    cleaning_fee: 20,
    catering_per_person: 15
  });

  const [amenities, setAmenities] = useState([
    { name: 'WiFi', included: true },
    { name: 'Projector', included: true },
    { name: 'Whiteboard', included: true },
    { name: 'Video Conference', included: false },
    { name: 'Coffee/Tea', included: false },
    { name: 'Parking', included: false }
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/meeting-rooms/rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hotel_id: hotelId,
          name: formData.name,
          room_type: formData.room_type,
          capacity: parseInt(formData.capacity),
          description: formData.description,
          floor: formData.floor,
          size_sqft: parseInt(formData.size_sqft),
          amenities: amenities,
          pricing: {
            hourly_rate: parseFloat(formData.hourly_rate),
            half_day_rate: parseFloat(formData.half_day_rate),
            full_day_rate: parseFloat(formData.full_day_rate),
            setup_fee: parseFloat(formData.setup_fee),
            cleaning_fee: parseFloat(formData.cleaning_fee),
            catering_per_person: parseFloat(formData.catering_per_person)
          }
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Meeting room created successfully!');
        onSuccess();
        onClose();
        // Reset form
        setFormData({
          name: '',
          room_type: 'boardroom',
          capacity: 10,
          description: '',
          floor: 'Ground',
          size_sqft: 200,
          hourly_rate: 50,
          half_day_rate: 180,
          full_day_rate: 300,
          setup_fee: 25,
          cleaning_fee: 20,
          catering_per_person: 15
        });
      } else {
        toast.error(data.error || 'Failed to create room');
      }
    } catch (error) {
      console.error('Error creating room:', error);
      toast.error('Failed to create meeting room');
    } finally {
      setLoading(false);
    }
  };

  const toggleAmenity = (index) => {
    const updated = [...amenities];
    updated[index].included = !updated[index].included;
    setAmenities(updated);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">Add Meeting Room</DialogTitle>
          <p className="text-sm text-muted-foreground">Create a new meeting space for bookings</p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="font-semibold">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Room Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Boardroom A"
                  required
                />
              </div>
              <div>
                <Label htmlFor="room_type">Room Type *</Label>
                <Select value={formData.room_type} onValueChange={(val) => setFormData({...formData, room_type: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="boardroom">Boardroom (10-20 people)</SelectItem>
                    <SelectItem value="conference">Conference Hall (50-100 people)</SelectItem>
                    <SelectItem value="banquet">Banquet Hall (100+ people)</SelectItem>
                    <SelectItem value="small">Small Meeting Room (4-8 people)</SelectItem>
                    <SelectItem value="breakout">Breakout Room (flexible)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="capacity">Capacity (people) *</Label>
                <Input
                  id="capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({...formData, capacity: e.target.value})}
                  min="1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="floor">Floor</Label>
                <Input
                  id="floor"
                  value={formData.floor}
                  onChange={(e) => setFormData({...formData, floor: e.target.value})}
                  placeholder="e.g., Ground, 2nd Floor"
                />
              </div>
              <div>
                <Label htmlFor="size">Size (sq ft)</Label>
                <Input
                  id="size"
                  type="number"
                  value={formData.size_sqft}
                  onChange={(e) => setFormData({...formData, size_sqft: e.target.value})}
                  min="0"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe the room, its features, and ideal use cases..."
                rows={3}
              />
            </div>
          </div>

          <Separator />

          {/* Pricing */}
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold">Pricing (USD)</h3>
              <p className="text-sm text-muted-foreground">Perfect for tech workers, freelancers, and on-demand meetings!</p>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="hourly">Hourly Rate * ⏰</Label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-muted-foreground">$</span>
                  <Input
                    id="hourly"
                    type="number"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                    className="pl-7"
                    step="0.01"
                    min="0"
                    required
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Per hour rate</p>
              </div>
              <div>
                <Label htmlFor="half_day">Half Day (4 hrs) 🕐</Label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-muted-foreground">$</span>
                  <Input
                    id="half_day"
                    type="number"
                    value={formData.half_day_rate}
                    onChange={(e) => setFormData({...formData, half_day_rate: e.target.value})}
                    className="pl-7"
                    step="0.01"
                    min="0"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Discounted rate</p>
              </div>
              <div>
                <Label htmlFor="full_day">Full Day (8+ hrs) 📅</Label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-muted-foreground">$</span>
                  <Input
                    id="full_day"
                    type="number"
                    value={formData.full_day_rate}
                    onChange={(e) => setFormData({...formData, full_day_rate: e.target.value})}
                    className="pl-7"
                    step="0.01"
                    min="0"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Best value</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="setup">Setup Fee</Label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-muted-foreground">$</span>
                  <Input
                    id="setup"
                    type="number"
                    value={formData.setup_fee}
                    onChange={(e) => setFormData({...formData, setup_fee: e.target.value})}
                    className="pl-7"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="cleaning">Cleaning Fee</Label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-muted-foreground">$</span>
                  <Input
                    id="cleaning"
                    type="number"
                    value={formData.cleaning_fee}
                    onChange={(e) => setFormData({...formData, cleaning_fee: e.target.value})}
                    className="pl-7"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="catering">Catering/Person</Label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-muted-foreground">$</span>
                  <Input
                    id="catering"
                    type="number"
                    value={formData.catering_per_person}
                    onChange={(e) => setFormData({...formData, catering_per_person: e.target.value})}
                    className="pl-7"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Amenities */}
          <div className="space-y-4">
            <h3 className="font-semibold">Amenities</h3>
            <div className="grid grid-cols-2 gap-3">
              {amenities.map((amenity, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`amenity-${index}`}
                    checked={amenity.included}
                    onChange={() => toggleAmenity(index)}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <Label htmlFor={`amenity-${index}`} className="cursor-pointer">
                    {amenity.name}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Room
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Add Booking Dialog Component
function AddBookingDialog({ open, onClose, hotelId, hotelName, rooms, selectedRoom, currentUser, onSuccess }) {
  const { addToCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [formData, setFormData] = useState({
    room_id: selectedRoom?.id || '',
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    company_name: '',
    event_type: 'meeting',
    booking_date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '10:00',
    attendees: 1,
    needs_catering: false,
    needs_projector: false,
    needs_whiteboard: false,
    special_requests: '',
    is_recurring: false,
    recurrence_pattern: 'weekly',
    recurrence_end_date: ''
  });

  const [pricingPreview, setPricingPreview] = useState(null);

  useEffect(() => {
    if (selectedRoom) {
      setFormData(prev => ({...prev, room_id: selectedRoom.id}));
    }
  }, [selectedRoom]);

  useEffect(() => {
    if (formData.room_id && formData.start_time && formData.end_time) {
      calculatePricing();
    }
  }, [formData.room_id, formData.start_time, formData.end_time, formData.attendees, formData.needs_catering]);

  const calculatePricing = () => {
    const room = rooms.find(r => r.id === formData.room_id);
    if (!room) return;

    const startHour = parseInt(formData.start_time.split(':')[0]);
    const endHour = parseInt(formData.end_time.split(':')[0]);
    const startMin = parseInt(formData.start_time.split(':')[1]);
    const endMin = parseInt(formData.end_time.split(':')[1]);
    
    const duration = (endHour - startHour) + (endMin - startMin) / 60;

    let bookingType = 'hourly';
    let basePrice = 0;

    if (duration >= 8) {
      bookingType = 'full_day';
      basePrice = room.pricing.full_day_rate;
    } else if (duration >= 4) {
      bookingType = 'half_day';
      basePrice = room.pricing.half_day_rate;
    } else {
      bookingType = 'hourly';
      basePrice = room.pricing.hourly_rate * duration;
    }

    const setupFee = room.pricing.setup_fee || 0;
    const cleaningFee = room.pricing.cleaning_fee || 0;
    const cateringCost = formData.needs_catering ? (room.pricing.catering_per_person * formData.attendees) : 0;
    const totalPrice = basePrice + setupFee + cleaningFee + cateringCost;

    setPricingPreview({
      duration,
      bookingType,
      basePrice,
      setupFee,
      cleaningFee,
      cateringCost,
      totalPrice
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/meeting-rooms/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          hotel_id: hotelId,
          attendees: parseInt(formData.attendees),
          created_by: currentUser?.email || currentUser?.id
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Booking created successfully!');
        onSuccess();
        onClose();
      } else {
        toast.error(data.error || 'Failed to create booking');
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    // Validate required fields
    if (!formData.room_id || !formData.guest_name || !formData.guest_email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setAddingToCart(true);

    try {
      const room = rooms.find(r => r.id === formData.room_id);
      if (!room) {
        toast.error('Room not found');
        return;
      }

      // Calculate duration
      const startHour = parseInt(formData.start_time.split(':')[0]);
      const endHour = parseInt(formData.end_time.split(':')[0]);
      const startMin = parseInt(formData.start_time.split(':')[1]);
      const endMin = parseInt(formData.end_time.split(':')[1]);
      const duration = (endHour - startHour) + (endMin - startMin) / 60;

      // Get hotel name (assuming it's available in parent component)
      // For now, we'll use a placeholder - this should be passed as prop
      

      // Prepare cart item data
      const cartItemData = {
        hotel_id: hotelId,
        hotel_name: hotelName || 'Hotel',
        room_id: formData.room_id,
        room_name: room.name,
        booking_date: formData.booking_date,
        start_time: formData.start_time,
        end_time: formData.end_time,
        duration_hours: duration,
        attendees: parseInt(formData.attendees),
        needs_catering: formData.needs_catering,
        needs_projector: formData.needs_projector,
        needs_whiteboard: formData.needs_whiteboard,
        base_price: pricingPreview?.totalPrice || 0
      };

      // Add to cart
      const result = await addToCart('meeting_room', cartItemData);
      
      if (result.success) {
        onClose();
        // Reset form would happen on close
      }
    } catch (error) {
      console.error('Add to cart error:', error);
    } finally {
      setAddingToCart(false);
    }
  };

  const selectedRoomData = rooms.find(r => r.id === formData.room_id);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">Create Meeting Room Booking</DialogTitle>
          <p className="text-sm text-muted-foreground">Perfect for freelancers, remote workers, and on-demand meetings!</p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Room Selection */}
          <div className="space-y-4">
            <h3 className="font-semibold">Meeting Room</h3>
            <div>
              <Label htmlFor="room">Select Room *</Label>
              <Select 
                value={formData.room_id} 
                onValueChange={(val) => setFormData({...formData, room_id: val})}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a meeting room" />
                </SelectTrigger>
                <SelectContent>
                  {rooms.map((room) => (
                    <SelectItem key={room.id} value={room.id}>
                      {room.name} - Capacity: {room.capacity} people (${room.pricing.hourly_rate}/hr)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedRoomData && (
                <p className="text-xs text-muted-foreground mt-1">
                  💰 Hourly: ${selectedRoomData.pricing.hourly_rate} | Half Day: ${selectedRoomData.pricing.half_day_rate} | Full Day: ${selectedRoomData.pricing.full_day_rate}
                </p>
              )}
            </div>
          </div>

          <Separator />

          {/* Guest Info */}
          <div className="space-y-4">
            <h3 className="font-semibold">Guest Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="guest_name">Name *</Label>
                <Input
                  id="guest_name"
                  value={formData.guest_name}
                  onChange={(e) => setFormData({...formData, guest_name: e.target.value})}
                  placeholder="John Doe"
                  required
                />
              </div>
              <div>
                <Label htmlFor="guest_email">Email</Label>
                <Input
                  id="guest_email"
                  type="email"
                  value={formData.guest_email}
                  onChange={(e) => setFormData({...formData, guest_email: e.target.value})}
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <Label htmlFor="guest_phone">Phone</Label>
                <Input
                  id="guest_phone"
                  value={formData.guest_phone}
                  onChange={(e) => setFormData({...formData, guest_phone: e.target.value})}
                  placeholder="+1 234 567 8900"
                />
              </div>
              <div>
                <Label htmlFor="company">Company Name</Label>
                <Input
                  id="company"
                  value={formData.company_name}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  placeholder="Acme Inc."
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Booking Details */}
          <div className="space-y-4">
            <h3 className="font-semibold">Booking Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="event_type">Event Type *</Label>
                <Select value={formData.event_type} onValueChange={(val) => setFormData({...formData, event_type: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="meeting">Business Meeting</SelectItem>
                    <SelectItem value="conference">Conference</SelectItem>
                    <SelectItem value="training">Training Session</SelectItem>
                    <SelectItem value="interview">Job Interview</SelectItem>
                    <SelectItem value="workshop">Workshop</SelectItem>
                    <SelectItem value="party">Private Party</SelectItem>
                    <SelectItem value="wedding">Wedding</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="attendees">Number of Attendees *</Label>
                <Input
                  id="attendees"
                  type="number"
                  value={formData.attendees}
                  onChange={(e) => setFormData({...formData, attendees: e.target.value})}
                  min="1"
                  max={selectedRoomData?.capacity || 100}
                  required
                />
                {selectedRoomData && formData.attendees > selectedRoomData.capacity && (
                  <p className="text-xs text-red-600 mt-1">⚠️ Exceeds room capacity ({selectedRoomData.capacity})</p>
                )}
              </div>
            </div>
          </div>

          <Separator />

          {/* Date & Time */}
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold">Date & Time ⏰</h3>
              <p className="text-sm text-muted-foreground">Book by the hour - perfect for quick meetings!</p>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="booking_date">Date *</Label>
                <Input
                  id="booking_date"
                  type="date"
                  value={formData.booking_date}
                  onChange={(e) => setFormData({...formData, booking_date: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
              <div>
                <Label htmlFor="start_time">Start Time *</Label>
                <Input
                  id="start_time"
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="end_time">End Time *</Label>
                <Input
                  id="end_time"
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                  required
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Recurring Bookings */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_recurring"
                checked={formData.is_recurring}
                onChange={(e) => setFormData({...formData, is_recurring: e.target.checked})}
                className="w-4 h-4 rounded border-gray-300"
              />
              <Label htmlFor="is_recurring" className="cursor-pointer">
                🔄 <strong>Recurring Booking</strong> - Perfect for regular meetings!
              </Label>
            </div>

            {formData.is_recurring && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="recurrence_pattern">Repeat Every *</Label>
                    <Select 
                      value={formData.recurrence_pattern || 'weekly'} 
                      onValueChange={(val) => setFormData({...formData, recurrence_pattern: val})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Week (Same day/time)</SelectItem>
                        <SelectItem value="biweekly">2 Weeks</SelectItem>
                        <SelectItem value="monthly">Month</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="recurrence_end">End Date *</Label>
                    <Input
                      id="recurrence_end"
                      type="date"
                      value={formData.recurrence_end_date || ''}
                      onChange={(e) => setFormData({...formData, recurrence_end_date: e.target.value})}
                      min={formData.booking_date}
                      required={formData.is_recurring}
                    />
                  </div>
                </div>
                <div className="text-sm text-blue-800 bg-blue-100 p-3 rounded">
                  <strong>📅 Example:</strong> If you book for Monday 9-10 AM and select "Weekly" until next month, 
                  we'll automatically create bookings for every Monday at the same time!
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* Add-ons */}
          <div className="space-y-4">
            <h3 className="font-semibold">Add-ons</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="catering"
                  checked={formData.needs_catering}
                  onChange={(e) => setFormData({...formData, needs_catering: e.target.checked})}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <Label htmlFor="catering" className="cursor-pointer">
                  <Coffee className="w-4 h-4 inline mr-1" />
                  Catering Service {selectedRoomData && `($${selectedRoomData.pricing.catering_per_person}/person)`}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="projector"
                  checked={formData.needs_projector}
                  onChange={(e) => setFormData({...formData, needs_projector: e.target.checked})}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <Label htmlFor="projector" className="cursor-pointer">
                  <Projector className="w-4 h-4 inline mr-1" />
                  Projector & Screen
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="whiteboard"
                  checked={formData.needs_whiteboard}
                  onChange={(e) => setFormData({...formData, needs_whiteboard: e.target.checked})}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <Label htmlFor="whiteboard" className="cursor-pointer">
                  Whiteboard & Markers
                </Label>
              </div>
            </div>
            <div>
              <Label htmlFor="special_requests">Special Requests</Label>
              <Textarea
                id="special_requests"
                value={formData.special_requests}
                onChange={(e) => setFormData({...formData, special_requests: e.target.value})}
                placeholder="Any special requirements or notes..."
                rows={2}
              />
            </div>
          </div>

          <Separator />

          {/* Pricing Preview */}
          {pricingPreview && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Pricing Summary
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Duration:</span>
                  <span className="font-semibold">{pricingPreview.duration.toFixed(1)} hours</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Booking Type:</span>
                  <Badge variant="outline" className="font-semibold">
                    {pricingPreview.bookingType === 'hourly' ? '⏰ Hourly' : 
                     pricingPreview.bookingType === 'half_day' ? '🕐 Half Day' : 
                     '📅 Full Day'}
                  </Badge>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Base Price:</span>
                  <span className="font-semibold mono">${pricingPreview.basePrice.toFixed(2)}</span>
                </div>
                {pricingPreview.setupFee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Setup Fee:</span>
                    <span className="mono">${pricingPreview.setupFee.toFixed(2)}</span>
                  </div>
                )}
                {pricingPreview.cleaningFee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Cleaning Fee:</span>
                    <span className="mono">${pricingPreview.cleaningFee.toFixed(2)}</span>
                  </div>
                )}
                {pricingPreview.cateringCost > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Catering ({formData.attendees} people):</span>
                    <span className="mono">${pricingPreview.cateringCost.toFixed(2)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total Price:</span>
                  <span className="text-primary mono">${pricingPreview.totalPrice.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading || addingToCart}>
              Cancel
            </Button>
            <Button 
              type="button" 
              variant="outline"
              className="border-primary/40 hover:bg-primary/5"
              onClick={handleAddToCart}
              disabled={loading || addingToCart || !formData.room_id}
              data-testid="meeting-room-add-to-cart-button"
            >
              {addingToCart ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Add to Cart
                </>
              )}
            </Button>
            <Button type="submit" disabled={loading || addingToCart || !formData.room_id}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirm Booking
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
